import {
  HOST_BASE_URL,
  IS_BROWSER,
  IS_MAIN_PROD,
  KAKAO_JS_KEY,
  KAKAO_REDIRECT_URI,
  KAKAO_REST_API_KEY,
} from 'infra/constants'

declare global {
  interface Window {
    Kakao?: {
      init: (key: string) => void
      isInitialized: () => boolean
      Auth: {
        authorize: (options: {
          redirectUri: string
          state?: string
          [key: string]: unknown
        }) => void
        logout: (callback?: () => void) => void
      }
      Share: {
        sendDefault: (options: {
          objectType: string
          content: {
            title: string
            description: string
            imageUrl: string
            link: { mobileWebUrl: string; webUrl: string }
          }
          buttons: Array<{
            title: string
            link: { mobileWebUrl: string; webUrl: string }
          }>
        }) => void
      }
    }
  }
}

export interface KakaoShareParams {
  title: string
  description: string
  imageUrl: string
  link: string
  btnTitle: string
}

const getKakao = () => (IS_BROWSER ? window.Kakao : undefined)

export const getKakaoShareParams = ({
  title,
  description,
  imageUrl,
  link,
  btnTitle,
}: KakaoShareParams) => {
  return {
    title,
    description,
    imageUrl: `${HOST_BASE_URL}${imageUrl}`,
    link: `${HOST_BASE_URL}${link}`,
    btnTitle,
  }
}

export const ensureKakaoInitialized = () => {
  const kakao = getKakao()
  if (!kakao) return undefined
  if (!kakao.isInitialized()) {
    if (!KAKAO_JS_KEY) {
      console.warn('[kakao] Missing NEXT_PUBLIC_KAKAO_JS_KEY for SDK init')
      return kakao
    }
    kakao.init(KAKAO_JS_KEY)
  }
  return kakao
}

class Kakao {
  login(redirectUrl?: string) {
    const kakao = ensureKakaoInitialized()
    const targetUrl = redirectUrl || window.location.href
    if (kakao?.Auth?.authorize) {
      kakao.Auth.authorize({
        redirectUri: KAKAO_REDIRECT_URI,
        state: targetUrl,
      })
      return
    }
    window.location.href = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${KAKAO_REST_API_KEY}&redirect_uri=${KAKAO_REDIRECT_URI}&state=${encodeURIComponent(
      targetUrl,
    )}`
  }
  logout() {
    if (!IS_MAIN_PROD) return
    const kakao = ensureKakaoInitialized()
    if (kakao?.Auth?.logout) {
      kakao.Auth.logout()
      return
    }
    window.location.href = `https://kauth.kakao.com/oauth/logout?client_id=${KAKAO_REST_API_KEY}&logout_redirect_uri=${HOST_BASE_URL}`
  }
  share({ title, description, imageUrl, link, btnTitle }: KakaoShareParams) {
    const kakao = ensureKakaoInitialized()
    if (kakao?.Share?.sendDefault) {
      kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title,
          description,
          imageUrl,
          link: {
            mobileWebUrl: link,
            webUrl: link,
          },
        },
        buttons: [
          {
            title: btnTitle,
            link: {
              mobileWebUrl: link,
              webUrl: link,
            },
          },
        ],
      })
      return
    }
    window.open(
      `https://story.kakao.com/share?url=${encodeURIComponent(link)}`,
      '_blank',
    )
  }
}

export const kakao = new Kakao()
